// The places stream's content (gate C4): the kit's piece definitions, the lab's placements and water, the light
// models and the places look, read from their tables (content/tables/kit-pieces.json, places-lab.json, water-lab.json,
// places-light.json, places-look.json) into the shapes the packages take.
import type * as THREE from "three";
import { createPalette, type LowPolySceneLook } from "@rpg/look";
import type { PieceDef } from "@rpg/meshkit";
import type { SpikeContent } from "../content";
import type { KitPieceRow, PlacementRow, PlacesLightRow, PlacesLookRow, WaterBodyRow } from "../content/types.gen";

export type { PlacementRow, PlacesLightRow, PlacesLookRow, WaterBodyRow } from "../content/types.gen";

/** Everything the places lab reads from the content. */
export interface PlacesContent {
  /** Piece name → the kit's definition. */
  defs: Record<string, PieceDef>;
  placements: readonly PlacementRow[];
  water: readonly WaterBodyRow[];
  lights: Readonly<Record<string, PlacesLightRow>>;
  look: PlacesLookRow;
  /** The outdoor scene look (the valley's day look; the sky stream's time of day replaces it). */
  scene: LowPolySceneLook;
  /** The scene's palette with the kit's colours added. */
  palette: (c: string) => THREE.Color;
}

/** Read the places tables. Throws on a missing row the lab needs. */
export function placesContent(content: SpikeContent): PlacesContent {
  const reg = content.registry;
  const defs: Record<string, PieceDef> = {};
  for (const row of reg.rows("kit-pieces") as readonly KitPieceRow[]) {
    const { id, reason: _r, ...def } = row;
    defs[id] = def as unknown as PieceDef;
  }
  const look = reg.get("places-look", "default") as PlacesLookRow | undefined;
  if (!look) throw new Error("places: the places-look table has no 'default' row");
  const scene = reg.get("looks", "valley-day") as unknown as LowPolySceneLook | undefined;
  if (!scene) throw new Error('places: no look "valley-day"');
  const lights: Record<string, PlacesLightRow> = {};
  for (const r of reg.rows("places-light") as readonly PlacesLightRow[]) lights[r.id] = r;
  return {
    defs,
    placements: reg.rows("places-lab") as readonly PlacementRow[],
    water: reg.rows("water-lab") as readonly WaterBodyRow[],
    lights,
    look,
    scene,
    palette: createPalette({ ...scene.palette, ...(look.palette as Record<string, string>) }),
  };
}
